import { ImageIcon, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AssetSelectZoneProps {
  onSelectFromAssets: () => void
  onRemove?: () => void
  previewUrl: string | null
}

export function AssetSelectZone({ onSelectFromAssets, onRemove, previewUrl }: AssetSelectZoneProps) {
  if (previewUrl) {
    return <SelectedPreview previewUrl={previewUrl} onRemove={onRemove} onChangeAsset={onSelectFromAssets} />
  }

  return <SelectPrompt onSelect={onSelectFromAssets} />
}

function SelectedPreview({ previewUrl, onRemove, onChangeAsset }: {
  previewUrl: string
  onRemove?: () => void
  onChangeAsset: () => void
}) {
  return (
    <div className="relative h-[280px] rounded-xl overflow-hidden bg-black border border-white/[0.12]">
      <img
        src={previewUrl}
        alt="Selected asset"
        className="w-full h-full object-contain"
      />
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-3 right-3 p-1.5 rounded-lg bg-black/60 text-white/70 hover:text-white hover:bg-black/80 transition-colors z-10"
        >
          <X className="size-4" />
        </button>
      )}
      <button
        type="button"
        onClick={onChangeAsset}
        className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm opacity-0 hover:opacity-100 transition-all cursor-pointer"
      >
        <span className="px-5 py-2.5 rounded-lg bg-white/[0.1] border border-white/[0.2] text-white text-sm font-medium hover:bg-white/[0.15] transition-colors">
          Change Image
        </span>
      </button>
    </div>
  )
}

function SelectPrompt({ onSelect }: { onSelect: () => void }) {
  return (
    <div className="space-y-2.5">
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'group relative flex flex-col items-center justify-center w-full min-h-[280px] rounded-xl border-2 border-dashed cursor-pointer transition-all duration-300',
          'border-white/25 bg-gradient-to-b from-white/[0.05] to-transparent hover:border-white/40 hover:from-white/[0.08]'
        )}
      >
        <CornerAccents />
        <div className="flex flex-col items-center gap-5">
          <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-white/[0.15] via-white/[0.05] to-white/[0.1]">
            <div className="relative p-5 rounded-2xl bg-[#0a0a0a] group-hover:bg-white/[0.08] transition-all duration-300">
              <div className="animate-float">
                <ImageIcon className="relative size-9 text-[#999] group-hover:text-white transition-colors" />
              </div>
            </div>
          </div>
          <div className="text-center">
            <p className="font-medium text-lg text-[#bbb] group-hover:text-white transition-colors">
              Select from Assets
            </p>
            <p className="text-sm mt-2 text-[#666]">
              Choose an image from your <span className="text-white/80 group-hover:text-white underline underline-offset-2 transition-colors">generated assets</span>
            </p>
          </div>
        </div>
      </button>
    </div>
  )
}

function CornerAccents() {
  const baseClasses = "absolute w-6 h-6 border-white/30 group-hover:border-white/50 transition-all"

  return (
    <>
      <div className={cn(baseClasses, "top-3 left-3 border-t-2 border-l-2 rounded-tl-lg")} />
      <div className={cn(baseClasses, "top-3 right-3 border-t-2 border-r-2 rounded-tr-lg")} />
      <div className={cn(baseClasses, "bottom-3 left-3 border-b-2 border-l-2 rounded-bl-lg")} />
      <div className={cn(baseClasses, "bottom-3 right-3 border-b-2 border-r-2 rounded-br-lg")} />
    </>
  )
}
